import type { RedirectEntry } from '@frontend/types/adminhub';
import generatedRegistry from '../../generated/redirect-registry.json';
import { URL_ROLES, type UrlRole } from './serviceUrlRegistry';

interface GeneratedAuth {
  launchMode?: string;
  logoutMode?: string;
  launchPath?: string;
  fallbackLoginPath?: string;
  ssoEntryPath?: string;
}

interface GeneratedRedirectItem {
  service: string;
  id?: string;
  authModel?: string;
  keycloakClientId?: string | null;
  authVia?: string | null;
  publicPath?: string | null;
  paths?: Partial<Record<UrlRole, string>>;
  entryPath?: string;
  ssoEntryPath?: string;
  callbackPaths?: string[];
  expectedLandingPath?: string;
  auth?: GeneratedAuth;
}

function absUrl(origin: string, path: string | null | undefined): string | null {
  if (!path) {
    return null;
  }
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  const base = origin.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Build one Admin Hub redirect row per platform service from the generated
 * platform-routes registry (path-relative, resolved against the live origin).
 * Every service appears — Keycloak-OIDC services carry their callback contract;
 * identity-provider, gitea-federated, and no-auth services show
 * their public URL and auth model so the registry is exhaustive.
 *
 * `paths` / `roleUrls` expose every resolvable role the portal may use by reference.
 */
export function redirectEntriesFromManifest(origin: string): RedirectEntry[] {
  const items = (generatedRegistry.entries ?? []) as GeneratedRedirectItem[];
  return items.map((item) => {
    const callbacks = item.callbackPaths ?? [];
    const paths = item.paths ?? {};
    const entryPath =
      item.entryPath ?? paths.launch ?? paths.public ?? item.publicPath ?? null;
    const authModel = item.authModel ?? (item.keycloakClientId ? 'keycloak_oidc' : 'none');
    const roleUrls: Partial<Record<string, string>> = {};
    for (const role of URL_ROLES) {
      const p = paths[role];
      if (p) {
        const abs = absUrl(origin, p);
        if (abs) roleUrls[role] = abs;
      }
    }
    return {
      service: item.service,
      id: item.id,
      authModel,
      keycloakClientId: item.keycloakClientId ?? null,
      entryUrl: absUrl(origin, entryPath),
      callbackUrl: callbacks.length
        ? absUrl(origin, callbacks[0])
        : absUrl(origin, paths.callback),
      expectedLandingUrl: absUrl(
        origin,
        item.expectedLandingPath ?? paths.landing ?? entryPath ?? undefined
      ),
      keycloakRegisteredRedirectUris: callbacks
        .map((p) => absUrl(origin, p))
        .filter((u): u is string => u !== null),
      paths,
      roleUrls,
    };
  });
}
